import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'learner'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      const userDoc = {
        uid: user.uid,
        email: user.email,
        displayName: formData.displayName || formData.email.split('@')[0],
        role: formData.role,
        status: 'active',
        isActive: true,
        isVerified: false,
        createdAt: serverTimestamp(),
        enrolledDate: serverTimestamp(),
        
        // Role-specific defaults
        ...(formData.role === 'learner' && {
          cohortId: '',
          programId: '',
          progress: {
            completedModules: [],
            currentModule: null,
            overallProgress: 0
          },
          documents: {}
        }),
        
        ...(formData.role === 'facilitator' && {
          managedCohorts: [],
          canCreateContent: true,
          canGradeAssignments: true
        }),
        
        ...(formData.role === 'admin' && {
          permissions: {
            manageUsers: true,
            manageSystem: true,
            manageContent: true,
            viewReports: true,
            manageSettings: true
          }
        }),
        
        // Profile information
        profile: {
          firstName: formData.displayName?.split(' ')[0] || '',
          lastName: formData.displayName?.split(' ').slice(1).join(' ') || '',
          phone: '',
          address: '',
          department: ''
        }
      };
      
      await setDoc(doc(db, 'users', user.uid), userDoc);
      
      // Navigate based on role
      switch (formData.role) {
        case 'admin':
          navigate('/admin');
          break;
        case 'facilitator':
          navigate('/facilitator');
          break;
        default:
          navigate('/dashboard');
      }
      
    } catch (err) {
      setError(err.message || 'Failed to create an account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-md-center">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h2 className="text-center mb-4">Sign Up</h2>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form onSubmit={handleSignup}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={formData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    placeholder="Enter your full name"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email address"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter a secure password (min 6 characters)"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select 
                    value={formData.role} 
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  >
                    <option value="learner">Learner</option>
                    <option value="facilitator">Facilitator</option>
                    <option value="admin">Administrator</option>
                  </Form.Select>
                </Form.Group>
                
                <Button 
                  className="w-100 mt-3" 
                  type="submit" 
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? 'Creating Account...' : 'Sign Up'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          <div className="w-100 text-center mt-2">
            Already have an account? <Link to="/login">Log In</Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default SignupPage;